import { useState } from "react";
import { router } from "@inertiajs/react";
import SettingsLayout from "@/components/SettingsLayout";
import { SettingsList, SettingsListItem } from "@/components/SettingsList";
import { formatTimestamp } from "@/lib/dates";

interface McpTokenSummary {
  id: string;
  name: string;
  last_used_at: string | null;
  created_at: string;
}

interface Props {
  tokens: McpTokenSummary[];
  newToken: string | null;
}

const SETUP_HINT =
  'claude mcp add --transport http revolver https://revolvertodo.duckdns.org/mcp --header "Authorization: Bearer <token>"';

const inputClass =
  "w-full sm:flex-1 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20";

export default function McpTokens({ tokens, newToken }: Props) {
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);

  function submitNewToken(e: React.FormEvent) {
    e.preventDefault();
    router.post("/settings/mcp_tokens", { name: name.trim() });
    setName("");
  }

  function revoke(id: string) {
    router.delete(`/settings/mcp_tokens/${id}`);
  }

  async function copyToken() {
    if (!newToken) return;
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable (e.g. insecure context); the token stays
      // visible on screen so it can still be copied by hand.
    }
  }

  return (
    <SettingsLayout active="mcp_tokens" title="API / MCP">
      <div className="space-y-2 text-sm text-gray-500">
        <p>
          MCP-коннектор даёт внешним AI-инструментам — Claude Code, Cursor и любому MCP-клиенту — доступ к твоим
          задачам через протокол MCP (Model Context Protocol).
        </p>
        <p className="text-amber-600 dark:text-amber-500">
          Токен даёт полный доступ ко всем задачам этого аккаунта. Храни его как пароль и не публикуй.
        </p>
        <pre className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 overflow-x-auto whitespace-pre-wrap break-all text-xs">
          {SETUP_HINT}
        </pre>
      </div>

      {newToken && (
        <div className="border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-md p-4 space-y-2">
          <div className="font-medium text-green-800 dark:text-green-300">Новый токен создан 🎉</div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <code className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-sm break-all">
              {newToken}
            </code>
            <button
              type="button"
              onClick={copyToken}
              className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-md shrink-0"
            >
              {copied ? "Скопировано ✓" : "Копировать"}
            </button>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
            Показывается один раз — сохрани сейчас.
          </p>
        </div>
      )}

      <form onSubmit={submitNewToken} className="flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Название, например Claude Code"
          aria-label="Название токена"
          className={inputClass}
        />
        <button
          type="submit"
          className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium shrink-0"
        >
          + Новый токен
        </button>
      </form>

      {tokens.length === 0 ? (
        <p className="text-sm text-gray-500">
          Токенов пока нет — создай первый выше и подключи Claude Code или Cursor одной командой ✨
        </p>
      ) : (
        <SettingsList>
          {tokens.map((t) => (
            <SettingsListItem
              key={t.id}
              title={t.name}
              meta={
                <>
                  Создан {formatTimestamp(t.created_at)}
                  {t.last_used_at && <> · использовался {formatTimestamp(t.last_used_at)}</>}
                </>
              }
              action={
                <button
                  type="button"
                  onClick={() => revoke(t.id)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Отозвать
                </button>
              }
            />
          ))}
        </SettingsList>
      )}
    </SettingsLayout>
  );
}
